import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { getUser, signIn as sendSignInRequest } from '../api/auth';
import type { User, AuthContextType } from '../types';
import { HUB_ACCESS_TOKEN_KEY, HUB_AUTH_EXPIRED_EVENT } from '../api/axiosClient';
import { useQueryClient } from '@tanstack/react-query';

function AuthProvider(props: React.PropsWithChildren<unknown>) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async function () {
      const result = await getUser();
      if (result.isOk) {
        setUser(result.data);
      }

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const expireSession = () => {
      queryClient.clear();
      setUser(undefined);
    };
    window.addEventListener(HUB_AUTH_EXPIRED_EVENT, expireSession);
    return () => window.removeEventListener(HUB_AUTH_EXPIRED_EVENT, expireSession);
  }, [queryClient]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await sendSignInRequest(email, password);
    if (result.isOk) {
      queryClient.clear();
      setUser(result.data);
    }

    return result;
  }, [queryClient]);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(HUB_ACCESS_TOKEN_KEY);
    queryClient.clear();
    setUser(undefined);
  }, [queryClient]);


  return (
    <AuthContext.Provider value={{ user, signIn, signOut, loading }} {...props} />
  );
}

const AuthContext = createContext<AuthContextType>({ loading: false } as AuthContextType);
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth }
